import { getApiErrorMessage } from '@components/common/util/util';
import { Config } from '@components/sources/source/warehouseSource/configureData';
import { IColumn } from '@components/sources/source/warehouseSource/configureData/sourceSpecificConfig';
import { apiAuthCaller } from '@services/apiCaller';
import { IDestination } from '@stores/destination';
import { ISource } from '@stores/source';
import useStores from '@stores/useStores';
import { CatchErr, FixMe } from '@utils/types';
import { Skeleton } from 'antd';
import { sortBy, uniq } from 'lodash';
import * as React from 'react';
import { fetchErrorAssets } from './errorsView';
import ConnectionConfig from './connectionConfig';
import { skipSchemaConfig } from './sourceUtil';
import ConnectionProvider, {
  ConnectionProps,
  ConnectionState,
  IField,
  IMappingConfig,
  IObject,
  ISourceConfig,
} from './visualDataMappingProvider';

export const SYNC_BEHAVIOUR_MODES = {
  UPSERT: 'upsert',
  MIRROR: 'mirror',
};
export interface SourceSettingsProps extends Config {
  accountId?: string;
  schedule?: unknown;
  query?: {
    table?: {
      schema?: string;
      name?: string;
    };
    columns?: string[];
  };
  mappings?: { original: string; mapped: string }[];
  newFields?: IField[];
  object?: IObject;
  sqlModelId?: string;
}

interface IAddMappingProps {
  source?: ISource;
  destination?: Partial<IDestination>;
  ref?: FixMe;
  ifUpdate?: boolean;
  sourceSettings?: SourceSettingsProps;
  onChange?: (config: SourceSettingsProps, isValid?: boolean) => void;
  viewOnly?: boolean;
}
export const isCompleteSourceConfig = (config: ISourceConfig, skipSchemaConfiguration?: boolean) =>
  Boolean(
    !skipSchemaConfiguration
      ? (config?.table?.schema && config?.table?.name) || config.sqlModelId
      : config?.table?.bucketName,
  );

export default React.forwardRef((props: IAddMappingProps & ConnectionProps, ref) => {
  const { onChange, sourceSettings, ifUpdate, viewOnly, updateSchemaErrors, ...restProps } = props;
  const [config, setConfig] = React.useState<Partial<ConnectionState>>({});

  const { userStore, workspaceStore, messagesStore, sourceDataStore, modelListStore } = useStores();
  const skipSchemaConfiguration = skipSchemaConfig(restProps.source.sourceDef.name);
  const selectedSqlModel = sourceSettings?.sqlModelId
    ? modelListStore.getModelById(sourceSettings?.sqlModelId)
    : undefined;
  if (ref) {
    React.useImperativeHandle(ref, () => ({
      isValidConfiguredData() {
        const completeSourceConfig =
          config.sourceConfig &&
          isCompleteSourceConfig(config.sourceConfig, skipSchemaConfiguration);
        if (!completeSourceConfig) {
          return 'Source configuration is incomplete';
        }
        const completeDestinationConfig = !!config?.destinationConfig?.object;
        if (!completeDestinationConfig) {
          return 'Destination configuration is incomplete';
        }
        let emptyColumns = 0;
        let invalidColumns = 0;
        let isEmptyUniqIdentifier = false;
        let invalidUniqIdentifier = false;
        const allMappings: IMappingConfig[] = [
          config?.uniqueIdentifierConfig || {},
          ...(config?.mappingConfig || [{}]),
        ];
        allMappings?.forEach((curr: IMappingConfig, index) => {
          if (index === 0 && (!curr.original || !curr.mapped)) {
            isEmptyUniqIdentifier = true;
          } else if (
            index === 0 &&
            config.columnMap &&
            curr.original &&
            !config.columnMap[curr.original]
          ) {
            invalidUniqIdentifier = true;
          } else if (index && !curr.original) {
            emptyColumns += 1;
          } else if (
            index &&
            curr.original &&
            config.columnMap &&
            !config.columnMap[curr.original]
          ) {
            invalidColumns += 1;
          }
        });

        setConfig({ ...config, enteredSaveState: true });
        const invalidColumnsError = invalidColumns ? `${invalidColumns} invalid columns` : '';

        const emptyColumnsError = emptyColumns ? `${emptyColumns} unmapped fields` : '';
        let errorMessage = '';
        if (isEmptyUniqIdentifier) {
          errorMessage = 'Unique identifier is empty';
        } else if (invalidUniqIdentifier) {
          errorMessage = 'Selected unique identifier not found in the warehouse';
        } else if (invalidColumns && emptyColumns) {
          errorMessage = `${invalidColumnsError} and ${emptyColumnsError}`;
        } else {
          errorMessage = invalidColumnsError || emptyColumnsError;
        }
        return errorMessage;
      },
    }));
  }
  const onChangeConfig = React.useCallback(
    (conf: Partial<ConnectionState>) => {
      const newConfig = { ...config, ...conf };
      setConfig(newConfig);
      const userIdMapping = {
        original: newConfig?.uniqueIdentifierConfig?.original,
        mapped: 'user_id',
      };
      const extraMappings: IMappingConfig[] = [
        { original: newConfig?.uniqueIdentifierConfig?.original, mapped: 'user_id' },
        {
          original: newConfig?.uniqueIdentifierConfig?.original,
          mapped: 'context.externalId[0].id',
        },
      ];
      const mapConfig = newConfig?.mappingConfig || [];
      const newFields = newConfig?.fields?.reduce((accum: IField[], field: IField) => {
        const { createdNow, ...restFieldProps } = field;
        if (createdNow) {
          accum.push(restFieldProps);
        }
        return accum;
      }, []);
      const constants = [
        {
          key: 'context.externalId[0].identifierType',
          value: newConfig?.uniqueIdentifierConfig?.mapped,
        },
        {
          key: 'context.externalId[0].type',
          value: `${restProps?.destination?.destinationDefinition?.name}-${newConfig?.destinationConfig?.object?.name}`,
        },
        {
          key: 'context.mappedToDestination',
          value: 'true',
        },
      ];
      /* this logic gets all the field names (mapped) from mappings and uniqueIdentifierConfig
      and create a new constant mirrorModeConstant */
      if (newConfig.syncBehaviour?.mode === SYNC_BEHAVIOUR_MODES.MIRROR) {
        const uniqFieldNames = [newConfig.uniqueIdentifierConfig || {}, ...mapConfig].map(
          (curr: IMappingConfig) => curr.mapped,
        );
        const mirrorModeConstant = {
          key: 'context.destinationFields',
          value: uniqFieldNames.join(', '),
        };

        constants.push(mirrorModeConstant);
      }
      const extraConfig = { syncBehaviour: { mode: newConfig.syncBehaviour?.mode } };

      let finalConfig: Record<string, unknown> = {
        accountId: sourceSettings?.accountId,
        event_type: 'identify',
        mappings: [...extraMappings, ...mapConfig],
        object: newConfig?.destinationConfig?.object,
        constants,
        schedule: sourceSettings?.schedule,
        newFields,
        ...extraConfig,
      };

      const columns = uniq(
        [userIdMapping, ...mapConfig]
          .map((m: IMappingConfig) => m.original)
          .filter((m?: string) => !!m),
      );

      if (skipSchemaConfiguration) {
        finalConfig = {
          ...finalConfig,
          bucketName: newConfig?.sourceConfig?.table?.bucketName,
          prefix: newConfig?.sourceConfig?.table?.prefix,
          columns,
          resources: ['bucket'],
        };
      } else if (newConfig.sourceConfig?.sqlModelId) {
        finalConfig = {
          ...finalConfig,
          query: {
            columns,
          },
          sqlModelId: newConfig.sourceConfig?.sqlModelId,
          resources: ['sql_model'],
        };
      } else {
        finalConfig = {
          ...finalConfig,
          query: {
            table: {
              schema:
                newConfig?.sourceConfig?.table?.schema || sourceSettings?.query?.table?.schema,
              name: newConfig?.sourceConfig?.table?.name || sourceSettings?.query?.table?.name,
            },
            columns,
          },
          resources: [newConfig?.sourceConfig?.table?.name || sourceSettings?.query?.table?.name],
        };
      }

      if (onChange) {
        onChange(finalConfig, true);
      }
    },
    [config, sourceSettings, onChange],
  );
  let sourceConfig: ISourceConfig = {};
  if (sourceSettings?.query?.table?.name || sourceSettings?.bucketName) {
    sourceConfig = {
      table: {
        name: sourceSettings?.query?.table?.name,
        schema: sourceSettings?.query?.table?.schema,
        bucketName: sourceSettings?.bucketName,
        prefix: sourceSettings?.prefix,
      },
    };
  } else {
    sourceConfig = { sqlModelId: sourceSettings?.sqlModelId };
  }

  interface Extract {
    objectConfig: string;
    uniqueIdentifierConfig: IMappingConfig;
    mappingConfig: IMappingConfig[];
  }

  let extractConfigs: Extract = (sourceSettings?.mappings || [])?.reduce(
    (accum: Extract, curr: IMappingConfig) => {
      if (curr?.mapped === 'user_id') {
        accum.uniqueIdentifierConfig.original = curr.original;
      } else if (curr?.mapped === 'anonmyous_id' || curr?.mapped === 'context.externalId[0].id') {
        // ignore the case
      } else {
        accum.mappingConfig.push(curr);
      }

      return accum;
    },
    { objectConfig: '', uniqueIdentifierConfig: {}, mappingConfig: [] },
  );

  extractConfigs = (sourceSettings?.constants || [])?.reduce(
    (accum: Extract, curr: { key: string; value: string }) => {
      if (curr?.key === 'context.externalId[0].type') {
        accum.objectConfig = curr.value?.substring(curr.value.lastIndexOf('-') + 1) || '';
      } else if (curr?.key === 'context.externalId[0].identifierType') {
        accum.uniqueIdentifierConfig.mapped = curr.value;
      }
      return accum;
    },
    extractConfigs,
  );

  const mappingConfig = extractConfigs?.mappingConfig;

  const uniqueIdentifierConfig = extractConfigs?.uniqueIdentifierConfig;

  const fetchFields = async () => {
    try {
      updateSchemaErrors([fetchErrorAssets('fields')]);
      const resp = await apiAuthCaller(userStore.token, false, 35000).post(
        `integrationsInfo/${restProps?.destination?.destinationDefinition?.name}/fields`,
        {
          config: {
            destinationId: restProps?.destination?.id,
            workspaceId: workspaceStore.id,
            ...restProps?.destination.config,
          },
          object: { name: extractConfigs.objectConfig },
        },
      );

      return resp.data.fields;
    } catch (err: CatchErr) {
      const errMsg = getApiErrorMessage(err, 'Failed to fetch the object fields');
      if (errMsg) {
        updateSchemaErrors([fetchErrorAssets('fields', errMsg)]);
        messagesStore?.showErrorMessage(errMsg);
      }

      return [];
    }
  };

  const fetchObjects = async () => {
    try {
      updateSchemaErrors([fetchErrorAssets('objects')]);
      const resp = await apiAuthCaller(userStore.token).post(
        `integrationsInfo/${restProps?.destination?.destinationDefinition?.name}/objects`,
        { config: { destinationId: restProps?.destination.id, ...restProps?.destination.config } },
      );
      return resp.data.objects;
    } catch (err: CatchErr) {
      const errMsg = getApiErrorMessage(err, 'Failed to fetch the destination objects');
      if (errMsg) {
        updateSchemaErrors([fetchErrorAssets('objects', errMsg)]);
        messagesStore?.showErrorMessage(errMsg);
      }
      return [];
    }
  };

  const fetchColumns = async () => {
    try {
      updateSchemaErrors([fetchErrorAssets('columns')]);
      const fetchColumnsParams = {
        schema: config?.sourceConfig?.table?.schema || sourceSettings?.query?.table?.schema,
        table: config?.sourceConfig?.table?.name || sourceSettings?.query?.table?.name,
        bucketName: config?.sourceConfig?.table?.bucketName || sourceSettings?.bucketName,
        prefix: config?.sourceConfig?.table?.prefix || sourceSettings?.prefix,
        sqlModelId: config.sourceConfig?.sqlModelId || sourceSettings?.sqlModelId,
      };
      const columns = await sourceDataStore.getColumns(
        {
          accountId: sourceSettings?.accountId || selectedSqlModel?.account.id,
          ...fetchColumnsParams,
        },
        restProps?.source?.sourceDef?.name,
      );

      return columns || [];
    } catch (err: CatchErr) {
      const errMsg = getApiErrorMessage(err, 'Failed to fetch columns');
      if (errMsg) {
        updateSchemaErrors([fetchErrorAssets('columns', errMsg)]);
        messagesStore?.showErrorMessage(errMsg);
      }
      return [];
    }
  };
  React.useEffect(() => {
    const fetchDetailsInUpdateMode = async () => {
      if ((viewOnly || ifUpdate) && !config?.fields && !config?.columns) {
        const object = (await fetchObjects()).find(
          (curr: IObject) => curr.name === extractConfigs?.objectConfig,
        );
        const fieldsProps: IField[] = await fetchFields();
        const columnsProps: IColumn[] = await fetchColumns();

        setConfig({
          ...config,
          columns: sortBy(columnsProps, 'name'),
          columnMap: columnsProps?.reduce(
            (accum: { [name: string]: IColumn }, column: IColumn) => ({
              ...accum,
              [column.name]: column,
            }),
            {},
          ),
          fields: fieldsProps,
          destinationConfig: { object },
          uniqueIdentifierConfig,
          mappingConfig,
          sourceConfig,
          syncBehaviour: sourceSettings?.syncBehaviour,
        });
      }
    };
    fetchDetailsInUpdateMode();
  }, []);
  return (ifUpdate || viewOnly) &&
    (!config?.fields || !config.columns || !config.destinationConfig) ? (
    <Skeleton active paragraph={{ rows: 4 }} />
  ) : (
    <ConnectionProvider
      modelAccountId={selectedSqlModel?.account.id}
      ifUpdate={!!ifUpdate}
      viewOnly={viewOnly}
      source={restProps.source}
      destination={restProps.destination}
      onChange={onChangeConfig}
      destinationConfig={config?.destinationConfig}
      sourceConfig={sourceConfig}
      mappingConfig={mappingConfig}
      uniqueIdentifierConfig={uniqueIdentifierConfig}
      fields={config?.fields}
      columns={config?.columns}
      enteredSaveState={config?.enteredSaveState}
      setBottomEditButtonVisible={restProps.setBottomEditButtonVisible}
      updateSchemaErrors={updateSchemaErrors}
      syncBehaviour={config?.syncBehaviour}
    >
      <ConnectionConfig />
    </ConnectionProvider>
  );
}) as React.FC<IAddMappingProps & ConnectionProps>;
